import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { QUERY_dashboardUpcomingShiftsPage } from 'queries';
import { SessionService } from 'services';
import { isOrderStatusInProgress, isOrderStatusUnfulfilled } from 'types/common.types';
import {
  DashboardUpcomingShiftsPageQuery,
  Order,
  OrderSortableFieldsEnum,
  SortDirectionEnum,
} from 'types/graphql';
import { isDateTimeAfterNow, isDateTimeBeforeNow } from 'utils/time';

import {
  DashboardPageUpcomingShiftsPayload,
  DashboardPageUpcomingShiftsPayloadTransformer as DPUSPT,
} from 'pages/DashboardUpcomingShiftsPage/DashboardUpcomingShiftsPage.types';
import { authLink, createApolloClient, httpLink } from 'providers/ApolloProvider';
import { DateTime } from 'luxon';
import { compact } from 'utils/compact';

const dashboardUpcomingShiftsPagePayloadTransformer: DPUSPT = ({
  isUserAgencyAndAgencyLocationFilterForcedDisabled,
  timeZone,
  ...filterValues
}) => {
  const variables: DashboardPageUpcomingShiftsPayload = {
    currentPage: filterValues.currentPage,
    first: filterValues.first,
    last: filterValues.last,
    after: filterValues.after,
    before: filterValues.before,
    filter: {
      forOrders: {
        withCommunity: undefined,
        withStatus: filterValues.shiftStatus,
        withPositions: filterValues.position_id,
        withJaneType: [...filterValues.internalJaneType],
        withStaff: {
          types: [...filterValues.externalJaneType],
          facility: {
            id: Number(filterValues.location.id),
            type: SessionService.assertUserType('B2B') ? 'Community' : 'AgencyCommunity',
          },
        },
        withTimeFrame: filterValues.timeFrameType,
        startBetweenTimeRange: {
          from: DateTime.now().setZone(timeZone).startOf('day').toString(),
          to: DateTime.now().setZone(timeZone).plus({ month: 1 }).toString(),
        },
        withActionsRequired: filterValues.actionsRequiredType,
      },
      forJanes: {
        byEmploymentStatus: filterValues.employmentStatus,
        byFullName: filterValues.byFullName,
      },
      forAgencyCommunities: {
        withId: undefined,
      },
    },
    sortOrders: [
      {
        field: OrderSortableFieldsEnum.startAt,
        direction: SortDirectionEnum.asc,
      },
      {
        field: OrderSortableFieldsEnum.positionName,
        direction: SortDirectionEnum.asc,
      },
    ],
  };
  if (!filterValues?.viewAllFacilities) {
    if (SessionService.assertUserType('B2B'))
      variables.filter.forOrders['withCommunity'] = Number(filterValues.location.value);

    if (
      SessionService.assertUserType('Agency') &&
      !isUserAgencyAndAgencyLocationFilterForcedDisabled
    )
      variables.filter.forAgencyCommunities['withId'] = Number(filterValues.location.value);
  }
  return variables;
};

export function dashboardUpcomingShiftsPageResponseTransformer(
  data: DashboardUpcomingShiftsPageQuery,
) {
  if (!data) return null;
  const newData: DashboardUpcomingShiftsPageQuery = JSON.parse(JSON.stringify(data));

  if (!newData) return null;
  if (!newData?.fastGroupOrders) return null;
  if (!newData?.fastGroupOrders?.nodes) return null;

  const inProgressData: DashboardUpcomingShiftsPageQuery = newData;
  const notInProgressData: DashboardUpcomingShiftsPageQuery = JSON.parse(JSON.stringify(data));

  if (!!inProgressData?.fastGroupOrders?.nodes && inProgressData?.fastGroupOrders?.nodes.length)
    inProgressData?.fastGroupOrders?.nodes.forEach((node) => {
      if (node) {
        const isToday = DateTime.fromISO(node.group).day === DateTime.now().day;
        if (isToday) {
          const unfulfilledOrders = [] as Order[];
          (node?.orders as Order[])?.forEach((record: Order, index2: number, arr2: Order[]) => {
            if (
              !isOrderStatusInProgress(record.status.value) &&
              isDateTimeBeforeNow(record.endAt)
            ) {
              delete arr2[index2];
            }

            if (isOrderStatusUnfulfilled(record.status.value)) {
              if (isDateTimeAfterNow(record.endAt)) unfulfilledOrders.push(record);
              delete arr2[index2];
            }
          });

          // @ts-ignore
          node.orders = [...node.orders, ...unfulfilledOrders];
        }
      }
    });

  if (
    !!notInProgressData?.fastGroupOrders?.nodes &&
    notInProgressData?.fastGroupOrders?.nodes.length
  )
    notInProgressData?.fastGroupOrders?.nodes.forEach((node) => {
      if (node) {
        (node?.orders as Order[])?.forEach((record: Order, index2: number, arr2: Order[]) => {
          if (isDateTimeBeforeNow(record.endAt) || isOrderStatusInProgress(record.status.value)) {
            delete arr2[index2];
          }
        });
      }
    });

  let result = {
    ...notInProgressData,
    ...inProgressData,
  };
  let filtered = {
    ...result,
    fastGroupOrders: {
      ...result.fastGroupOrders,
      nodes: result.fastGroupOrders.nodes
        .filter((record) => record.orders.length !== 0)
        .map((record) => {
          const { orders: _orders } = record;

          return { ...record, orders: compact(_orders) };
        }),
    },
  } as DashboardUpcomingShiftsPageQuery;

  (filtered.fastGroupOrders.nodes || []).forEach((record, index, arr) => {
    if (record.orders.length === 0) {
      delete arr[index];
    }
  });
  if (filtered.fastGroupOrders.nodes)
    filtered.fastGroupOrders.nodes = filtered.fastGroupOrders.nodes.filter(Boolean);
  return filtered;
}

const client = createApolloClient();

export const dashboardUpcomingShiftsPageConfig = ({
  location,
  isUserAgencyAndAgencyLocationFilterForcedDisabled,
}: {
  location: ILocationFilterProps['value'];
  isUserAgencyAndAgencyLocationFilterForcedDisabled: boolean;
}): ISSFBehaviorsOptions => {
  client.setLink(authLink.concat(httpLink()));
  return {
    key: 'DASHBOARD_UPCOMING-SHIFTS_PAGE',
    nextFetchPolicy: 'network-only',
    client,
    query: QUERY_dashboardUpcomingShiftsPage,
    responseTransformer: dashboardUpcomingShiftsPageResponseTransformer,
    pageSize: 20,
    payloadTransformer: (values) =>
      dashboardUpcomingShiftsPagePayloadTransformer({
        ...values,
        isUserAgencyAndAgencyLocationFilterForcedDisabled,
        timeZone: (location as { timeZone?: string })?.timeZone || undefined,
      }),
    schema: {
      position_id: {
        initialValue: [],
        isSessionCached: true,
      },
      shiftStatus: {
        initialValue: [],
        isSessionCached: true,
      },
      location: {
        initialValue: location,

        isGlobalCached: true,
        isPreservedOnReset: true,
        resetFieldsOnChange: [
          {
            field: 'position_id',
            userTypes: ['Agency'],
          },
        ],
      },
      employmentStatus: {
        initialValue: [],
        isSessionCached: true,
      },
      byFullName: {
        initialValue: '',
        isSessionCached: true,
      },
      internalJaneType: {
        initialValue: [],
        isSessionCached: true,
      },
      timeFrameType: {
        initialValue: [],
        isSessionCached: true,
      },
      externalJaneType: {
        initialValue: [],
        isSessionCached: true,
      },
      actionsRequiredType: {
        initialValue: [],
        isSessionCached: true,
      },
      viewAllFacilities: {
        initialValue: false,
        isGlobalCached: true,
        isSessionCached: true,
        isPreservedOnReset: true,
      },
    },
  };
};
